/**
 * ThemeContext — manages light/dark/auto theme preference.
 * - 'auto': follows OS prefers-color-scheme (default)
 * - 'light': always light
 * - 'dark': always dark
 * Applies 'dark' class to <html> and persists preference to localStorage.
 */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'

export type ThemeMode = 'auto' | 'light' | 'dark'

interface ThemeContextType {
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  isDark: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const STORAGE_KEY = 'themeMode'

function applyTheme(isDark: boolean) {
  if (isDark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    try {
      return (localStorage.getItem(STORAGE_KEY) as ThemeMode) || 'auto'
    } catch {
      return 'auto'
    }
  })

  // Track OS preference
  const [osPrefersDark, setOsPrefersDark] = useState(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches
  )

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setOsPrefersDark(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const isDark = mode === 'dark' || (mode === 'auto' && osPrefersDark)

  // Apply to <html> whenever it changes
  useEffect(() => {
    applyTheme(isDark)
  }, [isDark])

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode)
    try { localStorage.setItem(STORAGE_KEY, newMode) } catch {}
  }, [])

  return (
    <ThemeContext.Provider value={{ mode, setMode, isDark }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
